import { AclModel, TenantUserRoleReadModel, UserReadModel } from '@client/api';
import {
  changeDateTimeZone,
  changeI18nLanguage,
  resetDateTimeZone,
  resetI18nLanguage,
  TimeZone,
} from '@client/toolkit';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';

export const SESSION_FEATURE_KEY = 'session';
export enum TenantSelectionEnum {
  All = 'all',
}
export type SelectedTenant = TenantUserRoleReadModel | TenantSelectionEnum;
export interface SessionState {
  isAuthenticated: boolean;
  user: UserReadModel | null;
  selectedTenant: SelectedTenant | null;
  allowedTenants: TenantUserRoleReadModel[];
  language: string | null;
  timezone: string | null;
}

export const initialSessionState: SessionState = {
  isAuthenticated: false,
  user: null,
  selectedTenant: null,
  allowedTenants: [],
  language: null,
  timezone: null,
};

export const sessionSlice = createSlice({
  name: SESSION_FEATURE_KEY,
  initialState: initialSessionState,
  reducers: {
    clearSession(_state) {
      resetI18nLanguage();
      resetDateTimeZone();
      return initialSessionState; // https://redux-toolkit.js.org/usage/immer-reducers#resetting-and-replacing-state
    },
    setSession(state, action: PayloadAction<AclModel>) {
      state.user = action.payload?.user ?? null;
      state.allowedTenants = [...(action.payload?.allowedTenants ?? [])];
      state.selectedTenant = action.payload?.selectedTenant ?? null;
      state.isAuthenticated = true;
    },
    setSessionUser(state, action: PayloadAction<UserReadModel>) {
      state.user = action.payload ?? null;
    },
    setSessionLanguage(state, action: PayloadAction<string | null>) {
      state.language = action.payload ?? null;
      if (!action.payload) return;
      changeI18nLanguage(action.payload);
    },
    setSessionTimezone(state, action: PayloadAction<string | null>) {
      state.timezone = action.payload ?? null;
      if (!action.payload) return;
      changeDateTimeZone(action.payload as TimeZone);
    },
    setSelectedTenant(state, action: PayloadAction<SelectedTenant>) {
      state.selectedTenant = action.payload;
    },
  },
});

export const sessionReducer = sessionSlice.reducer;
export const sessionActions = sessionSlice.actions;

export const { clearSession, setSession, setSessionUser, setSessionLanguage, setSessionTimezone, setSelectedTenant } =
  sessionActions;

export const getSessionState = (rootState: RootState): SessionState => rootState[SESSION_FEATURE_KEY];

export const selectIsAuthenticated = createSelector(getSessionState, ({ isAuthenticated }) => isAuthenticated);
export const selectSessionUser = createSelector(getSessionState, ({ user }) => user);
export const selectSessionLanguage = createSelector(getSessionState, ({ language }) => language);
export const selectTenantSettings = createSelector(getSessionState, ({ selectedTenant }) => {
  if (!selectedTenant || selectedTenant === TenantSelectionEnum.All) return null;
  return selectedTenant.settings;
});
/**
 * The selected tenant only when exactly one is in scope. `null` when nothing is
 * selected yet or when the user is viewing "all tenants", so callers can gate
 * features that are meaningless across several tenants at once.
 */
export const selectSpecificTenant = createSelector(getSessionState, ({ selectedTenant }) => {
  if (!selectedTenant || selectedTenant === TenantSelectionEnum.All) return null;
  return selectedTenant;
});
export const selectAllowedTenants = createSelector(getSessionState, ({ allowedTenants }) => allowedTenants);
export const selectSelectedTenant = createSelector(getSessionState, ({ selectedTenant }) => selectedTenant);
